'use client'
import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { FaRegEdit, FaCaretDown, FaCaretUp, FaCloudUploadAlt } from 'react-icons/fa'
import { BsFileEarmarkImage, BsImages } from 'react-icons/bs'
import { TbDeviceWatchHeart } from 'react-icons/tb'
import { addDays, subDays, isBefore, isAfter, addHours } from 'date-fns'
import { 
  Input, NumberInput, Textarea, Button, Checkbox, DatePicker,
  Modal, ModalContent, ModalBody, ModalHeader, ModalFooter, Spinner } from "@heroui/react"
import { parseAbsoluteToLocal } from '@internationalized/date'
import { useRouter } from 'next/navigation'

import ErrorCard from './error-card'
import SuccessCard from './success-card'
import AppIcons from './app-icons'
import ErrorChip from './error-chip'
import ActivityTypeChip from './activity-type-chip'
import { inputStyleInside, calendarStyle } from '../src/styles'

import { converter, convertMilesToMeters, convertFeetToMeters, formatDate } from '../src/formatter'
import { activityTypeConfig, allActivityTypes, activityTypesDistance, activityTypesRepetitions } from '../src/activity-type-config'
import useActivity from '../hooks/use-activity'
import useChallengesJoined from '../hooks/use-challenges-joined'
import { useUser } from '../contexts/user-context-v2'
import UploadPanel from './upload-panel'
import { getSignedUrl, uploadToS3 } from './upload'

const getRelevantTypes = (challenges, activityType) => {
  let allActiveActivityTypeKeys = {}
  let allActiveActivityTypes = []
  let customActivityTypes = []
  let archivedCustomTypes = []
  
  challenges.forEach(c => {
    const cutOff = addDays(new Date(c.end), 10) // add 10 days to allow for 10 days of backfilling
    const dateValid = isBefore(new Date(), cutOff)
    if (c.points && c.points.custom && c.points.custom.length) {
      if (dateValid) {
        customActivityTypes = customActivityTypes.concat(c.points.custom.map(cp => { return { ...cp, challengeId: c.id, challengeName: c.challengeName, end: c.end } }))
      } else {
        archivedCustomTypes = archivedCustomTypes.concat(c.points.custom.map(cp => { return { ...cp, challengeId: c.id, challengeName: c.challengeName, end: c.end } }))
      }
    }
    if (dateValid) c.activityTypesArray.forEach(a => allActiveActivityTypeKeys[a] = true)
  })
  allActiveActivityTypes = Object.keys(allActiveActivityTypeKeys)

  if (!allActiveActivityTypes.length) {
    allActiveActivityTypes = allActivityTypes // no challenges joined, all are open for testing
  }
  
  let customActivityObj
  if (activityType && !allActivityTypes.includes(activityType)) {
    customActivityObj = customActivityTypes.find(item => item.activityType === activityType)
  }
  if (!customActivityObj) {
    customActivityObj = archivedCustomTypes.find(item => item.activityType === activityType)
  }

  return {
    allActiveActivityTypes,
    customActivityTypes,
    customActivityObj
  }
}

const ActivityTypeComponent = ({ challenges, data, existing, setData, errors, setErrors  }) => {
  const { allActiveActivityTypes, customActivityTypes } = getRelevantTypes(challenges, data.activityType)
  const [showMoreTypes, setShowMoreTypes] = useState()
  
  const handleActivityType = (value) => {
    const toUpdate = Object.assign({}, data)
    toUpdate.activityType = value
    if (!data.nameEdited && activityTypeConfig[value]) toUpdate.activityName = activityTypeConfig[value].title.default
    if (activityTypeConfig[value] && !activityTypeConfig[value].hasElevation) {
      toUpdate.elevation = ''
      toUpdate.elevationDisplay = ''
    }
    if (activityTypeConfig[value] && activityTypeConfig[value].type !== 'distance') {
      toUpdate.distance = ''
      toUpdate.distanceDisplay = ''
    }
    toUpdate.customActivityType = null
    toUpdate.isDistanceActivity = activityTypesDistance.includes(value)
    setData(toUpdate)
  }

  const handleCustomActivityType = (item) => {
    const toUpdate = Object.assign({}, data)
    toUpdate.activityType = item.activityType
    if (!data.nameEdited) toUpdate.activityName = item.activityType
    toUpdate.isDistanceActivity = item.measure === 'distance'
    if (item.measure !== 'distance') {
      toUpdate.distance = ''
      toUpdate.distanceDisplay = ''
    }
    toUpdate.elevation = ''
    toUpdate.elevationDisplay = ''
    toUpdate.customActivityType = item
    setData(toUpdate)
  }

  if (data.activityType && data.customActivityType && isAfter(new Date(), new Date(data.customActivityType.end))) {
    return <div>
      <div className='text-xl flex items-center mb-2 '>
        Activity Type
      </div>
      <div className='flex flex-wrap gap-1 w-full mb-4'>
        <ActivityTypeChip isSelected={true}>
          <Checkbox isSelected={true} />
          {data.activityType}
        </ActivityTypeChip>
        <div className='mt-2 mb-8 text-gray-400'>
          The custom activity type of <span className='text-secondary'>{data.activityType}</span> was only valid in <Link href={`/challenge/[id]`} as={`/challenge/${data.customActivityType.challengeId}`}>{data.customActivityType.challengeName}</Link> until <b>{formatDate(data.customActivityType.end, true)}</b>. Custom activity types cannot be changed after the challenge ends.
        </div> 
      </div>
    </div>
  }

  return (
    <>
      <div className='mb-2'>
        <div className='text-xl flex items-center mb-2 '>
          <ErrorChip errors={errors} setErrors={setErrors} errorKey='activityType' />
        </div>
        
        {/* Old crusty activity type */}
        {(data.activityType && !data.customActivityType && !allActivityTypes.includes(data.activityType)) && 
          <div className='flex flex-wrap gap-1 w-full'>
            <ActivityTypeChip isSelected={true}>
              <Checkbox color='secondary' classNames={{ wrapper: 'bg-white' }} isSelected={true} />
              {data.activityType}
            </ActivityTypeChip>
            <div className='mt-2 mb-8 text-gray-400 text-sm'>
              The custom activity type of <span className='text-secondary'>{data.activityType}</span> was not found in any challenges you&apos;ve joined. The type may have been removed or changed. Please select a new activity type.
            </div> 
          </div>
        }

        {/* Custom Types  */}
        {data.manual && Boolean(customActivityTypes.length) && 
          <div className="flex flex-wrap gap-1 w-full mb-4">
            {customActivityTypes.map((item, index) => {
              const isSelected = data.activityType.toLowerCase() === item.activityType.toLowerCase()
              return <ActivityTypeChip key={item} isSelected={isSelected}>
                  <Checkbox color='secondary' classNames={{ wrapper: 'bg-white' }} isSelected={isSelected} value={item} onChange={() => {
                    handleCustomActivityType(item)
                  }}>
                    <span className={`${isSelected ? 'text-white' : ''}`}>{item.activityType}</span>
                    <span className={`ml-2 text-sm ${isSelected ? 'text-gray-200' : 'text-gray-500'}`}>- {item.challengeName}</span>
                  </Checkbox>
              </ActivityTypeChip>
            })}
          </div>
        }

        <div className="flex flex-wrap gap-2 w-full">
          {allActivityTypes.filter(item => allActiveActivityTypes.includes(item) || item === existing?.activityType).map(type => {
            const isSelected = data.activityType.toLowerCase() === type
            return <ActivityTypeChip key={type} isSelected={isSelected}>
              <Checkbox color='secondary' classNames={{ wrapper: 'bg-white' }} isSelected={isSelected} value={type} onChange={() => {
                handleActivityType(type)
              }}>
                <span className={`${isSelected ? 'text-white' : ''}`}>{activityTypeConfig[type].title.default}</span>
              </Checkbox>
            </ActivityTypeChip>
          })}

          {Boolean(allActivityTypes.filter(type => {
            return !allActiveActivityTypes.includes(type) && type !== existing?.activityType
          }).length) && <div>
            <div className='flex justify-center my-2'>
              <Button size='sm' variant='light' onPress={() => setShowMoreTypes(!showMoreTypes)} startContent={showMoreTypes ? <FaCaretUp className='text-primary' style={{ fontSize: '16px'}} /> : <FaCaretDown className='text-primary' style={{ fontSize: '16px'}} />}>
                {showMoreTypes ? 'Hide Additional Activity Types' : 'Show More Activity Types'}
              </Button>
            </div>
            <div className={`flex flex-wrap gap-2 w-full overflow-hidden transition-max-height duration-500 ease-in-out max-h-[0px] ${showMoreTypes ? 'max-h-[425px]' : 'max-h-[0px]'}`}>
              {allActivityTypes.filter(type => !allActiveActivityTypes.includes(type) && type !== existing?.activityType).map(type => {
                const isSelected = data.activityType.toLowerCase() === type
                return <ActivityTypeChip key={type} isSelected={isSelected}>            
                  <Checkbox isSelected={isSelected} value={type} onChange={() => {
                    handleActivityType(type)
                  }}>
                    <span className={`${isSelected ? 'text-white' : ''}`}>{activityTypeConfig[type].title.default}</span>
                  </Checkbox>
                </ActivityTypeChip>
              })}
            </div>
          </div>}
          
          {data.activityType && !allActiveActivityTypes.includes(data.activityType) && 
            <div className='my-6 p-4 border-danger border-1 rounded-md'>
              {activityTypeConfig[data.activityType] && <span className='text-secondary font-bold'>{activityTypeConfig[data.activityType].title.default}</span>}
              {!activityTypeConfig[data.activityType] && <span className='text-secondary font-bold'>{data.activityType}</span>}
              &nbsp;is not a qualifying activity type for any of your active challenges. You can&nbsp;
              <Link href={`/challenge/new/edit`} as={`/challenge/new/edit`}>
                create&nbsp;
                {activityTypeConfig[data.activityType] && <>
                  {activityTypeConfig[data.activityType].title.default[0].match(/[aeiou]/i) ? 'an' : 'a'}&nbsp;
                  <b>{activityTypeConfig[data.activityType].title.default}</b>
                </>}
                {!activityTypeConfig[data.activityType] && <>
                  {data.activityType[0].match(/[aeiou]/i) ? 'an' : 'a'}&nbsp;
                  <b>{data.activityType}</b>
                </>}
                &nbsp;challenge.
              </Link>
            </div>
          }
        </div>
      </div>
    
    </>
  );
}

const origState = {
  id: '',
  activityName: '',
  activityType: '',
  start: new Date().toISOString(),
  manual: true,
  duration: '',
  repetitions: '',
  distance: '',
  elevation: '',
  distanceDisplay: '',
  elevationDisplay: '',
  durationTimeHours: 0,
  durationTimeMins: 0,
  durationTimeSecs: 0,
  useSeconds: true,
  customActivityType: null,
  nameEdited: false,
  isDistanceActivity: true,
  description: '',
  images: []
}

const ActivityForm = ({ modal, id, challengeData, activity, user, onSave }) => {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [images, setImages] = useState(new Map())
  const [success, setSuccess] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [data, setData] = useState(origState)
  
  const [postError, setPostError] = useState(false)
  const [errors, setErrors] = useState({})   
  const [fitLoaded, setFitLoaded] = useState(false)
  const [modalType, setModalType] = useState('panel')
  
  const router = useRouter()
  const fileUploadRef = useRef()
  const fitFileUploadRef = useRef()

  const { challenges } = challengeData

  useEffect(() => {
    if (modal.isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    if (!modal?.isOpen && !id || id !== activity.id) {
      setFitLoaded(false)
      setUploading(false)
      setData(origState)
    }
    const isMobileMedia = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    const isMobileCheck = typeof window !== 'undefined' && window.innerWidth <= 768 && 'ontouchstart' in window;
    setModalType((isMobileMedia || isMobileCheck) ? 'panel' : 'modal')
  }, [modal?.isOpen, activity.id, id]);
  
  useEffect(() => {
    if (challengeData.loading) return
    if (activity && activity.id && data.id !== activity.id) {
      let existing = Object.assign({}, activity)
      existing.distanceDisplay = Math.round(converter(existing.distance, user.imperial) * 100) / 100
      existing.elevationDisplay = Math.round(converter(existing.elevation, user.imperial, 'sm'))
      existing.start = new Date(existing.start).toISOString()
      
      if (activityTypesDistance.includes(existing.activityType)) {
        existing.isDistanceActivity = true
      }

      const { customActivityObj } = getRelevantTypes(challenges, existing.activityType)
      if (customActivityObj) {
        existing.customActivityType = customActivityObj
        existing.isDistanceActivity = customActivityObj.measure === 'distance'
      }

      if (existing.images) {
        const imagesMap = new Map()
        existing.images.forEach(img => {
          imagesMap.set(img, {
            key: img,
            url: `${process.env.CDN}/${img}`,
            contentType: img.split('.').pop().toLowerCase(),
            uploaded: true
          })
        })
        setImages(imagesMap)
      }
      
      const dp = durationParts(existing.duration)
      existing.nameEdited = true
      setData({ ...existing, ...dp })
    }
  }, [challenges, activity])

  const getMinStart = (data) => {
    const sixty = subDays(new Date(), 60)
    if (data && data.id && isBefore(new Date(data.start), sixty)) {
      return data.start
    }
    return sixty
  }

  const durationParts = (dur) => {
    if (dur) {
      const num = parseInt(dur, 10)
      let hours = Math.floor((num / 60) / 60)
      let minutes = Math.floor((num - (hours * 3600)) / 60)
      let seconds = num - (hours * 3600) - (minutes * 60)
      return {
        durationTimeHours: hours,
        durationTimeMins: minutes,
        durationTimeSecs: seconds,
        useSeconds: true
      }
    }
    return {
      durationTimeHours: 0,
      durationTimeMins: 0,
      durationTimeSecs: 0,
      useSeconds: true
    }
  }
  
  const transforms = {
    distanceDisplay: (toUpdate, value) => {
      if (value) {
        toUpdate.distance = user.imperial || user.imperial === null ? convertMilesToMeters(value) : value * 1000
      } else {
        toUpdate.distance = ''
      }
    },
    elevationDisplay: (toUpdate, value) => {
      if (value) {
        toUpdate.elevation = user.imperial || user.imperial === null ? convertFeetToMeters(value) : value
      } else {
        toUpdate.elevation = ''
      }
    },
    activityName: (toUpdate, value) => {
      if (value) toUpdate.nameEdited = true
    },
    durationTimeHours: (toUpdate, value) => {
      const h = value ? value * 3600 : 0
      const m = toUpdate.durationTimeMins * 60
      const s = toUpdate.durationTimeSecs
      toUpdate.duration = h + m + s
    },
    durationTimeMins: (toUpdate, value) => {
      let minutes = value ? value : 0
      
      if (minutes >= 60) {
        const additionalHours = Math.floor(minutes / 60)
        minutes = minutes % 60
        toUpdate.durationTimeHours = (toUpdate.durationTimeHours || 0) + additionalHours
        toUpdate.durationTimeMins = minutes
      }

      const h = toUpdate.durationTimeHours * 3600
      const m = value ? value * 60 : 0
      const s = toUpdate.durationTimeSecs
      toUpdate.duration = h + m + s
    },
    durationTimeSecs: (toUpdate, value) => {
      let seconds = value ? value : 0
      
      // Convert seconds to minutes if >= 60
      if (seconds >= 60) {
        const additionalMinutes = Math.floor(seconds / 60)
        seconds = seconds % 60
        toUpdate.durationTimeMins = (toUpdate.durationTimeMins || 0) + additionalMinutes
        toUpdate.durationTimeSecs = seconds
      }
      
      const h = toUpdate.durationTimeHours * 3600
      const m = toUpdate.durationTimeMins * 60
      const s = seconds
      toUpdate.duration = h + m + s
    },
  }

  const handleChange = (attr, value, isInt, isFloat) => {
    if (isInt && parseInt(value) < 0) return
    const toUpdate = Object.assign({}, data)
    const convertedValue = value ? (isInt ? parseInt(value, 10) : (isFloat ? parseFloat(value, 10) : value)) : ''
    toUpdate[attr] = convertedValue
    if (transforms && transforms[attr]) transforms[attr](toUpdate, convertedValue)
    setData(toUpdate)
    setSuccess(false)
    setPostError(false)
    setErrors({})
  }

  const validate = () => {
    const validation = {}
    if (!data.activityName) validation.activityName = 'Required'
    if (!data.activityType) validation.activityType = 'Required'
    if (!data.start) validation.start = 'Required'
    if (activityTypesRepetitions.includes(data.activityType) && !data.repetitions) {
      validation.repetitions = 'number of reps required'
    }
    
    const { customActivityObj } = getRelevantTypes(challenges, data.activityType)
    
    if ((allActivityTypes.includes(data.activityType) || (customActivityObj && customActivityObj.measure !== 'count')) && !data.duration) validation.duration = 'Required'
    if (data.distance < 0) validation.distance = 'Distance must be positive number or 0.'
    if (data.elevation < 0) validation.distance = 'Elevation must be positive number or 0.'
    if (data.distance > 400000) validation.distance = 'Max single activity is 400 km or 249 mi'
    if (data.activityType !== 'backcountryski' && data.activityType !== 'alpineski' && data.elevation > 6000) validation.elevation = 'Max single activity is 6,000 m or 19,685 ft'
    if ((data.activityType === 'backcountryski' || data.activityType === 'alpineski') && data.elevation > 10000) validation.elevation = 'Max single activity is 10,000 m or 32,808 ft'
    if ((data.activityType === 'snowboard' || data.activityType === 'snowboard') && data.elevation > 10000) validation.elevation = 'Max single activity is 10,000 m or 32,808 ft'
    
    if (!customActivityObj && !allActivityTypes.includes(data.activityType)) validation.activityType = `Type Required`
    
    if (Object.keys(validation).length) {
      console.log(validation)
      setErrors(validation)
      return false
    }
    return true
  }

  const submit = async () => {
    if (!validate()) return
    setSaving(true)
    
    const toSubmit = { ...data }
    delete toSubmit.customActivityType
    delete toSubmit.nameEdited
    delete toSubmit.isDistanceActivity
    delete toSubmit.challenges

    let imgs = []
    if (images.size > 0) {
      imgs = Array.from(images.values()).map(img => { 
        return img.key.replace('-originals', '')
      })
      toSubmit.images = imgs
    } else {
      toSubmit.images = []
    }

    try {
      const method = data.id ? 'PUT' : 'POST'
      const submitConfig = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        },
        body: JSON.stringify(toSubmit)
      }
      const res = await fetch(`${process.env.API}/activities`, submitConfig)
      if (!res.ok) {
        setSaving(false)
        const err = await res.text()
        setPostError(`Error saving the activity: ${err}`)
        return
      }
      const newActivity = await res.json()
      setSaving(false)
      onSave({
        id: 'fakeId', // for map, needs unique key
        createdAt: new Date().toISOString(),
        activity: { ...newActivity },
        user: {
          id: user.id,
          fullname: user.fullname,
          photo: user.photo
        },
        content: newActivity.description,
        images: newActivity.images
      })
    } catch (err) {
      console.log(err)
      if (err && err.data) {
        const validation = {}
        const [field, errMsg] = err.data.split(':')
        if (data[field]) {
          validation[field] = errMsg
          setErrors(validation)
        } else {
          setPostError(`Problem saving the activity: ${err.data}`)
        }
      } else {
        setPostError('Problem saving the activity')
      }
      setSaving(false)

    }
  }

  const deleteActivity = async () => {
    modal.onClose()
    setLoading(true)
    try {
      const deleteConfig = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        }
      }
      const res = await fetch(`${process.env.API}/activities/${data.id}`, deleteConfig)
      if (!res.ok) {
        setLoading(false)
        const errText = await res.text()
        setPostError(`Problem deleting the activity: ${errText}`)
        return
      }
      // Check if we can go back in history, and if so go back 2 steps
      if (window.history.length) {
        setTimeout(() => {
          router.back()
        }, 500) // delay to propogate deletion  
      } else {
        router.push(`/activities`)
      }
    } catch (err) {
      console.error(err)
      setPostError('Problem deleting this activity')
      setLoading(false)
    }
  }

  const onFitUpload = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setUploading(true)
    const file = e.target.files[0]
  
    const fileParts = file.name.split('.')
    const contentType = fileParts[fileParts.length - 1].toLowerCase()
    
    if (['fit'].indexOf(contentType) === -1) {
      setErrors({ fit: 'Invalid file type. Must be a FIT file.' })
      setUploading(false)
      return
    }

    try {
      const data = await getSignedUrl(user.token, file.name, 'fit', contentType, 'fit')
      await uploadToS3(data.signedRequest, 'fit', file)
      await getFitData(data.url)  
    } catch (err) {
      console.error(err)
      setUploading(false)
    }
  }

  const getFitData = async (url) => {
    setPostError('')
    setLoading(true)
    try {
      const postConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        },
        body: JSON.stringify({ url })
      }
      const res = await fetch(`${process.env.API}/fit-file`, postConfig)
      if (!res.ok) {
        const errText = await res.text()
        setPostError(`Problem getting fit data: ${errText}`)
        return
      }
      const fitData = await res.json()
      
      const toUpdate = Object.assign({}, fitData)
      toUpdate.activityName = fitData.activityName
      toUpdate.activityType = fitData.activityType || 'run'
      toUpdate.distance = fitData.distance
      toUpdate.elevation = fitData.elevation
      toUpdate.distanceDisplay = Math.round(converter(fitData.distance, user.imperial) * 100) / 100
      toUpdate.elevationDisplay = Math.round(converter(fitData.elevation, user.imperial, 'sm'))
      toUpdate.start = fitData.start
      toUpdate.duration = parseInt(fitData.duration)
      toUpdate.integrationActivityType = fitData.integrationActivityType
      toUpdate.integrationType = fitData.integrationType
      const dp = durationParts(fitData.duration)
      setData({ ...toUpdate, ...dp })
      setLoading(false)
      setFitLoaded(true)
    } catch (err) {
      setLoading(false)
      console.error(err)
      setPostError('Problem getting fit data')
    }
  }

  const now = new Date()
  return <Modal 
    className={modalType === 'panel' ? 'fixed inset-0 bottom-0 left-0 right-0 top-0 h-[100%] mt-0 mb-0 ml-0 mr-0 rounded-none' : 'max-h-[70%]'} 
    size={modalType === 'modal' ? 'lg' : ''} 
    isOpen={modal.isOpen} onOpenChange={modal.onOpenChange}>
    <ModalContent className='overflow-y-auto'>
      <ModalHeader className="flex flex-row gap-1">
        <div className='grow'>{`${activity.id ? 'Edit' : 'Add'} Activity`}</div>
        {!data.id && data.manual && !fitLoaded && <div className='mr-8 flex flex-row'>
          {uploading ? <Spinner size='sm' /> : <Button
            className='min-w[.25rem] h-[1.8rem] pb-0 pt-0'
            variant='light'
            startContent={<FaCloudUploadAlt className='text-primary text-2xl mr-1' />}
            onPress={() => {
              fitFileUploadRef.current.click()
            }} >FIT</Button>}
          <input
            ref={ fitFileUploadRef }
            type='file'
            onChange={onFitUpload} 
            className='hidden h-0 absolute'/>
        </div>}
      </ModalHeader>
      <ModalBody style={modalType === 'modal' ? { maxHeight: '60vh', overflowY: 'auto' } : {}}>

        {success && <SuccessCard>Your activity has been saved!</SuccessCard>}
        {postError && <ErrorCard className='rounded-md p-2 whitespace-normal h-auto'>Error processing your request: {postError}</ErrorCard>}
        {errors?.fit && <ErrorCard className='rounded-md p-2 whitespace-normal h-auto'>Error Uploading Fit: {errors.fit}</ErrorCard>}
        {!activity.id && <div>
          {fitLoaded && <div className='mb-6'>
            <BsFileEarmarkImage className='text-2xl float-start text-secondary mr-2' /> 
            <span className='text-chSubtext'>FIT file used to populated the form below</span>
          </div>}
        </div>}

        {!data.manual && <div className='grid gap-2 grid-cols-2'>
          <div><AppIcons user={user} activity={activity} /></div>
          {activity.integrationType === 'strava' && <div className='text-center'>
            <div className='flex justify-center'><FaRegEdit style={{ fontSize: '30px' }} /></div>
            <div>
              <a href={`http://www.strava.com/activities/${activity.integrationActivityId}/edit`} target='_blank' rel='noreferrer'>Edit in Strava</a>
            </div>
          </div>}
          {activity.integrationType === 'garmin' && <div className='text-center'>
            <div className='flex justify-center'><FaRegEdit style={{ fontSize: '30px' }} /></div>
            <div>
              <a href={`https://connect.garmin.com/modern/activity/manual/${activity.integrationActivityId}/edit`} target='_blank' rel='noreferrer'>Edit in Garmin</a>
            </div>
          </div>}
          {activity.integrationType === 'uacf' && <div className='text-center'>
            <div className='flex justify-center'><FaRegEdit style={{ fontSize: '30px' }} /></div>
            <div>
              <a href={`https://www.mapmyfitness.com/workout/edit/${activity.integrationActivityId}/`} target='_blank' rel='noreferrer'>Edit in Map My Fitness</a>
            </div>
          </div>}
          {activity.source && <div className='text-center'>
            <div className='flex justify-center'><TbDeviceWatchHeart style={{ fontSize: '30px', height: '30px' }} /></div>
            <div>{activity.source}</div>
          </div>}
        </div>}
                  
        {!data.manual && <div className='text-chSubtext text-sm my-2'>For activities that were tracked using a 3rd party app, you will have to edit the activity start, duration, distance, and elevation on the 3rd party app, which will then re-sync to Challenge Hound.</div>}
                    
        <Input
          label='Activity Name'
          name='activityName'
          maxLength={50}
          value={data.activityName}
          size='lg'
          radius='sm'
          className='mb-2'
          classNames={ inputStyleInside }
          isInvalid={Boolean(errors.activityName)}
          errorMessage={errors.activityName}
          onFocus={(e) => {
            setTimeout(() => {
              e.target.select()
            }, 100)
          }}
          onChange={(e) => {
            handleChange('activityName', e.target.value)
          }} />

        <DatePicker 
          aria-label='Activity Date and Time'
          size='lg'
          isReadOnly={!data.manual}
          showMonthAndYearPickers={false}
          selectorButtonPlacement='start'
          popoverProps={{ placement: 'bottom-start' }}
          minValue={parseAbsoluteToLocal(getMinStart(data).toISOString())}
          maxValue={parseAbsoluteToLocal(addHours(now, 3).toISOString())}
          value={parseAbsoluteToLocal(data.start || now.toISOString())}
          className='mb-2'
          classNames={ calendarStyle }
          isInvalid={Boolean(errors.start)}
          errorMessage={errors.start}
          onChange={(val) => {
            handleChange('start', val.toDate().toISOString())
          }}
        />
                  
        <ActivityTypeComponent 
          challenges={challenges}
          existing={activity}
          data={data} 
          user={user} 
          setData={setData} 
          errors={errors}
          setErrors={setErrors} />
                    
        {(!data.customActivityType || data.customActivityType.measure !== 'count') && 
          <div className='mb-2'>
            <div className='flex'>
              <NumberInput
                aria-label='Hours'
                name='hours'
                minValue={0}
                maxValue={30}
                value={data.durationTimeHours}
                size='lg'
                radius='sm'
                classNames={ inputStyleInside }
                isInvalid={Boolean(errors.durationTimeHours)}
                errorMessage={errors.distance}
                isReadOnly={!data.manual}
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.select()
                  }, 100)
                }}
                formatOptions={{
                  style: "unit",
                  unit: 'hour',
                  unitDisplay: modalType === "modal" ? "long" : "short",
                  maximumFractionDigits: 2
                }}
                onValueChange={(num) => {
                  handleChange('durationTimeHours', num, false, true)
                }}
              />
              
              <div className='text-2xl font-bold px-2 mt-2'>:</div>

              <NumberInput
                aria-label='Minutes'
                name='minutes'
                minValue={0}
                value={data.durationTimeMins}
                size='lg'
                radius='sm'
                classNames={ inputStyleInside }
                isInvalid={Boolean(errors.durationTimeMins)}
                errorMessage={errors.distance}
                isReadOnly={!data.manual}
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.select()
                  }, 100)
                }}
                formatOptions={{
                  style: "unit",
                  unit: 'minute',
                  unitDisplay: modalType === "modal" ? "long" : "short",
                  maximumFractionDigits: 2
                }}
                onValueChange={(num) => {
                  handleChange('durationTimeMins', num, false, true)
                }}
              />

              <div className='text-2xl font-bold px-2 mt-2'>:</div>
              
              <NumberInput
                aria-label='Seconds'
                name='seconds'
                minValue={0}
                value={data.durationTimeSecs}
                size='lg'
                radius='sm'
                classNames={ inputStyleInside }
                isInvalid={Boolean(errors.durationTimeSecs)}
                errorMessage={errors.distance}
                isReadOnly={!data.manual}
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.select()
                  }, 100)
                }}
                formatOptions={{
                  style: "unit",
                  unit: 'second',
                  unitDisplay: modalType === "modal" ? "long" : "short",
                  maximumFractionDigits: 2
                }}
                onValueChange={(num) => {
                  handleChange('durationTimeSecs', num, false, true)
                }}
              />
            </div>
          </div>
        }
                  
        {(!data.activityType || data.isDistanceActivity) &&
          <NumberInput
            label='Distance'
            name='distanceDisplay'
            minValue={0}
            maxValue={user?.imperial ? 249 : 400}
            value={data.distanceDisplay || null}
            size='lg'
            radius='sm'
            className='mb-2'
            classNames={ inputStyleInside }
            isInvalid={Boolean(errors.distance)}
            errorMessage={errors.distance}
            isReadOnly={activity.original?.id ? false : !data.manual && activity.id && activity.distance !== 0}
            formatOptions={{
              style: "unit",
              unit: user?.imperial ? 'mile' : 'kilometer',
              unitDisplay: "long",
              maximumFractionDigits: 2
            }}
            onFocus={(e) => {
              setTimeout(() => {
                e.target.select()
              }, 100)
            }}
            onValueChange={(num) => {
              handleChange('distanceDisplay', num, false, true)
            }} />
        }
        
        {(!data.activityType || (activityTypeConfig[data.activityType] && activityTypeConfig[data.activityType].hasElevation)) && 
          <NumberInput
            label={`${['snowboard', 'alpineski', 'backcountryski'].includes(data.activityType) ? 'Descent' : 'Ascent'} Elevation`}
            name='elevationDisplay'
            minValue={0}
            maxLength={8}
            value={data.elevationDisplay || null}
            size='lg'
            radius='sm'
            className='mb-2'
            classNames={ inputStyleInside }
            isInvalid={Boolean(errors.elevation)}
            errorMessage={errors.elevation}
            isReadOnly={activity.original?.id ? false : !data.manual && activity.id && activity.elevation !== 0}
            formatOptions={{
              style: "unit",
              unit: user?.imperial ? 'foot' : 'meter',
              unitDisplay: "long",
              maximumFractionDigits: 2
            }}
            onFocus={(e) => {
              setTimeout(() => {
                e.target.select()
              }, 100)
            }}
            onValueChange={(num) => {
              handleChange('elevationDisplay', num, true)
            }} />
        }

        <Textarea
          label='Optional Description'
          name='description'
          maxLength={1000}
          value={data.description || ''}
          size='lg'
          radius='sm'
          className='mb-2'
          classNames={ inputStyleInside }
          isInvalid={Boolean(errors.description)}
          errorMessage={errors.description}
          onChange={(e) => {
            handleChange('description', e.target.value)
          }} />

        <div className='mb-2'>
          {images.size < 4 && <div className='bg-chOffWhite border-1 border-default text-chTan-600 rounded-md p-2 mb-2 flex flex-row items-center cursor-pointer' onClick={() => {
            fileUploadRef.current.click()
          }}>
            <BsImages className='text-chTan-600 text-4xl mr-4' />
            Add Photos
          </div>}
          <UploadPanel user={user} uploadRef={fileUploadRef} images={images} setImages={setImages} />
        </div>

        {!activity.id && data.integrationType && <div style={{ marginBottom: '40px' }}>FIT file app: <strong>{data.integrationType}</strong></div>}

        
        <ModalFooter>
          <div className='w-full flex mb-4'>
            <div className='grow'>
              {(activity && activity.id) && <Button
                color={confirmDelete ? 'danger' : ''}
                variant='ghost'
                isDisabled={loading || Boolean(Object.keys(errors).length)}
                onPress={() => {
                  if (!confirmDelete) {
                    setConfirmDelete(true)
                  } else {
                    deleteActivity()
                  }
                }}
                >{confirmDelete ? 'Confirm?' : 'Delete'}</Button>}
                {confirmDelete && <Button
                  className='ml-2'
                  variant='ghost'
                  onPress={() => {
                    setConfirmDelete(false)
                  }}>Cancel</Button>}
            </div>

            <div className='justify-end'>
              <Button
                color='primary'
                isDisabled={loading || Boolean(Object.keys(errors).length)}
                onPress={submit}
                >{saving ? <><Spinner color='secondary' size='sm' className='ml-2' />Saving...</> : 'Save Activity'}</Button>
            </div>
          </div>  
        </ModalFooter>
      </ModalBody>
    </ModalContent>
  </Modal>
}

const ActivityEdit = ({ id, modal, onSave }) => {
  const { user } = useUser()
  
  const challengeData = useChallengesJoined(modal.isOpen ? user?.token : null)
  const activityHook = useActivity(modal.isOpen ? user?.token : null, id)
  const activity = activityHook.activity

  return <ActivityForm id={id} challengeData={challengeData} activity={activity} modal={modal} user={user} onSave={onSave} />
}

export default ActivityEdit
